import React from 'react';
import Select, { MultiValueGenericProps, components } from 'react-select';
import { ChevronDown } from 'lucide-react';
import { cn } from 'shadcn-ui';
import { MultiDropdownLayoutProps } from '../../../uibuilder/form/input/MultiDropdown';
import { Option } from '../../../uibuilder/form/input';
import ShadcnBaseInputLayout from '../../../uibuilder/form/layout/ShadcnBaseInputLayout';

export default function ShadcnMultiDropdownLayout(
  props: MultiDropdownLayoutProps,
) {
  const valueMap: any = [];

  (props.value || []).forEach((item) => {
    valueMap[item as any] = true;
  });

  const currentValue =
    props.value && props.options
      ? props.options.filter((option) => valueMap[option.value])
      : [];

  const formatGroupLabel = (data: any) => (
    <div>
      <span>{data.label}</span>
      <span>{data.options.length}</span>
    </div>
  );

  const multiValueContainer = ({
    selectProps,
    data,
  }: MultiValueGenericProps) => {
    const { label } = data;
    const allSelected = selectProps.value;
    const index = allSelected.findIndex(
      (selected: Option) => selected.label === label,
    );
    const isLastSelected = index === allSelected.length - 1;
    const labelSuffix = isLastSelected ? ` (${allSelected.length})` : ', ';
    return `${label}${labelSuffix}`;
  };

  return (
    <ShadcnBaseInputLayout
      name={props.name}
      errors={props.errors}
      label={props.label}
      labelClassName={props.labelClassName}
      hideLabel={props.hideLabel}
    >
      <Select
        name={props.name}
        unstyled
        menuPlacement="auto"
        classNames={{
          control: (state) =>
            cn(
              '!min-h-9 w-full min-w-0 !rounded-full !border !border-gray-200 !bg-gray-100 !pl-3 !pr-2 !py-1 !text-sm !shadow-none !outline-none flex items-center flex-wrap cursor-pointer',
              state.isFocused && '!border-blue-500',
              state.isDisabled && 'opacity-50 cursor-not-allowed',
            ),
          placeholder: () => 'text-input-placeholder truncate text-sm',
          menu: () =>
            'bg-popover text-popover-foreground mt-1 rounded-md border border-gray-200 shadow-md z-50 overflow-hidden',
          menuList: () => 'p-1 max-h-60 overflow-y-auto',
          option: (state) =>
            cn(
              'relative flex w-full cursor-default items-center rounded-sm py-2 px-3 text-sm select-none',
              state.isFocused && 'bg-gray-100 text-gray-700',
              state.isSelected && 'font-medium',
              state.isDisabled && 'pointer-events-none opacity-50',
            ),
          multiValue: () =>
            'bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 text-xs font-medium flex items-center gap-1',
          multiValueLabel: () => 'text-xs',
          multiValueRemove: () =>
            'hover:text-blue-900 cursor-pointer rounded-full',
          valueContainer: () =>
            'gap-1 px-0 text-ellipsis max-w-[90%] whitespace-nowrap overflow-hidden h-9 text-gray-700 leading-9',
          dropdownIndicator: () => 'text-gray-400 p-0 pr-1',
          indicatorsContainer: () => 'gap-1',
          groupHeading: () =>
            'text-muted-foreground px-2 py-1.5 text-xs font-medium',
          input: () =>
            'text-sm text-gray-700 [&_input]:!border-none [&_input]:!outline-none [&_input]:!shadow-none [&_input]:!h-auto [&_input]:!p-0 [&_input]:!caret-transparent',
          noOptionsMessage: () => 'text-sm text-gray-400 py-2 px-3',
        }}
        options={props.options}
        isClearable
        value={currentValue}
        placeholder={props.placeholder}
        isDisabled={props.isDisabled}
        isMulti
        formatGroupLabel={formatGroupLabel}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        onChange={(newValue: any) => {
          props.onChangeCallback({
            target: {
              value: newValue
                ? newValue.map((item: Option) => item.value)
                : null,
            },
          });
        }}
        components={{
          IndicatorSeparator: () => null,
          MultiValueContainer: multiValueContainer,
          DropdownIndicator: (indicatorProps) => (
            <components.DropdownIndicator {...indicatorProps}>
              <ChevronDown className="size-4 opacity-50" />
            </components.DropdownIndicator>
          ),
        }}
        data-testid={`${props.name}-dropdown`}
        data-test={`${props.name}-dropdown`}
      />
    </ShadcnBaseInputLayout>
  );
}
